import { useMemo } from 'react';
import { ApolloClient, FetchPolicy, useQuery } from '@apollo/client';
import moize from 'moize';
import { sortFacilityOfferingsData } from '@pass-frontend/common/offerings/util';
import {
  ClientFacilityOffering,
  FacilityOfferingsSearchFieldVariables,
} from '@pass-frontend/data-access/state-management/apollo';
import {
  DISTANCE_TAGS,
  GYM_SORT_CRITERIA,
  SORT_CRITERIA,
} from '@pass-frontend/data-access/types';
import { facilityOfferingsSearchQueryGql } from './facility-offerings-search-query.gql';

const memoizedSortFacilityOfferingsData = moize.maxSize(4)(
  sortFacilityOfferingsData,
);

const defaults: FacilityOfferingsSearchFieldVariables<
  ClientFacilityOffering,
  GYM_SORT_CRITERIA
> = {
  distanceKey: DISTANCE_TAGS.WITHIN_40_MILES,
  filterKeys: undefined,
  searchText: null,
  sort: true,
  sortFn: memoizedSortFacilityOfferingsData,
  sortFnArgs: [SORT_CRITERIA.DISTANCE],
  limit: 4,
  offset: 0,
};

const makeDashboardFacilitySearchQueryOptions = (
  client: ApolloClient<unknown> | undefined,
  variables: typeof defaults,
) => ({
  /** SSR PARAMS **/
  client,
  /** END SSR PARAMS **/
  fetchPolicy: 'cache-only' as FetchPolicy,
  variables,
});

export const useFacilityOfferingsSearchQuery = (
  client: ApolloClient<unknown> | undefined,
  options: typeof defaults,
  withTierIds = false,
  apolloOptions?: {
    skip?: boolean;
  },
) => {
  const {
    distanceKey,
    filterKeys,
    searchText,
    sort,
    sortFn,
    sortFnArgs,
    limit,
    offset,
  } = options;

  const gqlQuery = useMemo(
    () => facilityOfferingsSearchQueryGql(withTierIds),
    [withTierIds],
  );
  const queryOptions = useMemo(() => {
    const variables = {
      ...defaults,
      distanceKey,
      filterKeys,
      searchText,
      sort,
      sortFn,
      sortFnArgs,
      limit,
      offset,
    };
    return {
      ...makeDashboardFacilitySearchQueryOptions(client, variables),
      skip: apolloOptions?.skip,
    };
  }, [
    client,
    distanceKey,
    filterKeys,
    searchText,
    sort,
    sortFn,
    sortFnArgs,
    limit,
    offset,
    apolloOptions?.skip,
  ]);

  return useQuery<{
    facilityOfferingsSearch: {
      totalCount: number;
      matches: ClientFacilityOffering[];
    };
  }>(gqlQuery, queryOptions);
};
